import React from "react";
import { Card } from "luna-react/components/card";

import Tabs from "../common/Tabs";

import phishingEmails from "../../assets/images/tab-images/how_to_spot_a_phishing_email.jpg";
import suspiciousText from "../../assets/images/tab-images/suspicious_text.jpg";
import protectDevices from "../../assets/images/tab-images/protect_devices.jpg";
import strongPassword from "../../assets/images/tab-images/strong_password.jpg";
import socialMediaScam from "../../assets/images/tab-images/social_media_scam.jpg";

export const DataSafe = () => {
  return (
    <Card>
      <div className="section_header center-text">
        <h1 className="section_title">How to keep your data safe</h1>
        <span className="section_subtext">
          Learn more about how you can keep your data safe at home
        </span>
      </div>
      <Tabs items={tabContents} />
    </Card>
  );
};

export const tabContents = [
  {
    title: "Social media scams",
    content: (
      <div>
        <p>
          Social media has become a part of everyday life for a lot of us, so
          it’s important to know how to protect yourself against scams whilst
          using it.
        </p>
        <ul>
          <li>
            If an offer looks too good to be true then it might not be
            legitimate. Do a little research before clicking on it
          </li>
          <li>
            Look out for links that look official, but direct you to a fake
            login page
          </li>
          <li>
            If someone you know has shared something with you, check with them
            that they actually recommended it
          </li>
          <li>
            Check the spelling in the URL as misspelled brand names can send you
            somewhere completely different
          </li>
          <li>
            Don’t always rely on the secure padlock icon in the address bar. It
            only means the web traffic is private, but doesn’t guarantee that
            you can trust the company
          </li>
        </ul>
      </div>
    ),
    image: socialMediaScam,
  },
  {
    title: "Phishing - emails",
    content: (
      <div>
        <p>
          Phishing is when fraudsters try to steal personal data, like bank
          details, by impersonating someone trustworthy via a malicious email.
          By pretending to be a legitimate person or company, the fraudsters
          trick you into handing over personal details or visiting an insecure
          website.
        </p>
        <h4>Spotting a phishing email:</h4>
        <ol>
          <li>
            <b>Authority</b>:{" "}
            <i>Is the email claiming to be from someone official?</i> For
            example: your bank, a government organisation, your doctor?
            Fraudsters often pretend to be important people/organisations to
            trick people to do what they want.
          </li>
          <li>
            <b>Urgency</b>:{" "}
            <i>
              Does the email state that you have a limited amount of time to
              act?{" "}
            </i>
            Fraudsters look to threaten you with fines and negative consequences
            if you don’t act within their stated time frame.
          </li>
          <li>
            <b>Emotion</b>:{" "}
            <i>
              Does the email make you feel happy, excited, curious, or worried?{" "}
            </i>
            Fraudsters often use emotive language to entice you to want to find
            out more.
          </li>
          <li>
            <b>Scarcity</b>:{" "}
            <i>Is the email offering something in short supply?</i> – for
            example concert tickets that are sold out. Fraudsters often exploit
            fear of missing out and trick victims.
          </li>
          <li>
            <b>Current events</b>:{" "}
            <i>Are you expecting to see a message like this?</i> Fraudsters
            exploit current events to trick you to click on links. Perhaps it’s
            time for you to receive your tax refunds? Criminals may imitate
            HMRC, tricking you to click on links.
          </li>
        </ol>
        <h4>What to do if you receive a suspicious email?</h4>
        <ol>
          <li>
            Don’t click on any links, open any attachments, or forward the email
            to anyone else.
          </li>
          <li>
            Don’t reply to the email or use any contact details contained within
            it.
          </li>
          <li>
            If you are unsure whether the email is legitimate, get in touch with
            the company separately to confirm.
          </li>
          <li>
            Note: Sainsbury’s will never contact you via email asking for your
            personal information such as credit card information.
          </li>
        </ol>
        <h4>
          What if I receive an unusual email from one of Sainsbury’s brands?
        </h4>
        <p>
          If you receive a phishing email from any of Sainsbury’s brands you can
          forward this to the following mailboxes:
        </p>
        <p>
          Sainsbury’s:{" "}
          <a href="mailto:scams@sainsburys.co.uk">scams@sainsburys.co.uk</a>
        </p>
      </div>
    ),
    image: phishingEmails,
  },
  {
    title: "SMiShing – suspicious text messages",
    content: (
      <div>
        <p>
          SMiShing is when fraudsters try to get hold of your data like
          usernames, passwords and payment card details by pretending to be
          someone legitimate via text message.
        </p>
        <p>
          The text message may include links to websites that are infected with
          malware or the link will ask you for personal data.
        </p>
        <h4>Points to remember if you receive an unusual SMS:</h4>
        <ol>
          <li>
            Companies/banks do not send text messages asking for personal
            information
          </li>
          <li>
            If you receive a suspicious text message, don’t click on any links,
            download, or open any attachments.
          </li>
          <li>Delete the text message and do not reply.</li>
          <li>
            Sainsbury’s would never ask for personal data in an SMS or email
            message. If you receive a message and are unsure, please call us on
            our official telephone number.
          </li>
        </ol>
        <h4>Reporting a SMiSh?</h4>
        <p>
          If you receive any unusual messages, forward this message to 7726,
          this will enable your service provider to investigate the origin of
          the message and take action if it’s fraudulent. This service is free
          of charge.
        </p>
      </div>
    ),
    image: suspiciousText,
  },
  {
    title: "Creating a strong password",
    content: (
      <div>
        <p>
          Creating a strong password is crucial to ensure basic security of your
          accounts. Below are top tips to create a strong password.
        </p>
        <ol>
          <li>Consider using a phrase or three random words.</li>
          <li>
            Don’t use the same password for all your accounts, you could risk
            having all your accounts hacked if a website is breached.
          </li>
          <li>
            Don’t use people’s names, as these are easily cracked using common
            hacking tools.
          </li>
          <li>
            Avoid using passwords that can be guessed after research on your
            online presence such as social media accounts. For example, pet
            names
          </li>
          <li>
            Avoid writing down passwords or sharing with anyone. If you find
            remembering your passwords difficult, consider using a secure
            password manager to store your passwords.
          </li>
        </ol>
      </div>
    ),
    image: strongPassword,
  },
  {
    title: "Managing your digital footprint",
    content: (
      <div>
        <p>
          Social Media has become a large part of everyday life, so it is
          important to know how to protect yourself online from scams.
        </p>
        <p>
          It’s important that you review your privacy settings online, to review
          who has access to your social media accounts.
        </p>
        <h4>What is your digital footprint?</h4>
        <p>
          Your digital footprint is all the stuff you leave behind as you use
          the Internet. Comments on social media, app use and email records –
          it’s part of your online history and can potentially be seen by other
          people or tracked in a database.
        </p>
        <h4>Below are some tips to manage your digital footprint</h4>
        <ol>
          <li>
            Try typing your name in a search engine, if you come across your
            social media profile and it is on public, you may want to review
            your privacy settings.
          </li>
          <li>
            Create strong passwords, three random words, this is easier to
            remember and harder to break. Ensure you don’t reuse your passwords
            across accounts.
          </li>
          <li>
            Take control: often the content online may not be reflective of how
            we truly are. For example, we may be tagged in a post we have
            nothing to do with. In such cases un-tag, yourself from posts you no
            longer want to be associated with.
          </li>
          <li>
            Enable two factor authentication (2FA) on your accounts: having
            strong passwords alone is not enough to secure your online accounts.
            By enabling 2FA on your accounts you are providing an extra layer of
            security as you are required to verify your identity and confirm you
            are who you say you are. Click{" "}
            <a href="https://www.ncsc.gov.uk/cyberaware/home#action-4">here</a>{" "}
            for more advice from the NCSC on how to set up 2FA on your accounts.
          </li>
        </ol>
      </div>
    ),
  },
  {
    title: "Protecting your devices",
    content: (
      <div>
        <p>
          It is important you protect yourself from online attacks. Below are
          some tips to prevent malware infecting your devices.
        </p>
        <ol>
          <li>
            When using smart devices only download and install programs from
            websites and app stores you trust – for example Apple or Android App
            Store.
          </li>
          <li>Only buy trusted and well known, subscription-based programs.</li>
          <li>
            Read security information, privacy statements and license agreements
            when you install software.
          </li>
          <li>
            Don’t click ‘OK’ or ‘I Agree’ on pop-up windows, unless you know
            what you’re signing up for.
          </li>
          <li>
            Ensure you update your devices and software on a regular basis. They
            may contain security updates that help protect your devices from
            cyber criminals.
          </li>
        </ol>
        <h4>Useful links</h4>
        <p>
          <div>
            The ICO (Information Commissioner’s Office) upholds information
            rights in the public interest, promoting openness by public bodies
            and data privacy for individuals.
          </div>
          <a
            rel="noopener noreferrer"
            target="_blank"
            href="https://ico.org.uk/for-the-public/"
          >
            https://ico.org.uk/for-the-public/
          </a>
        </p>
        <p>
          <div>
            Cyberaware is a UK Government resource focussed on making “good
            cyber security habits second nature.”
          </div>
          <a
            rel="noopener noreferrer"
            target="_blank"
            href="https://www.cyberaware.gov.uk/"
          >
            https://www.cyberaware.gov.uk/
          </a>
        </p>
        <p>
          <div>
            Get Safe Online offer easy to understand advice on how to protect
            your online self.
          </div>
          <a
            rel="noopener noreferrer"
            target="_blank"
            href="https://www.getsafeonline.org/"
          >
            https://www.getsafeonline.org/
          </a>
        </p>
      </div>
    ),
    image: protectDevices,
  },
];

export default DataSafe;



// WEBPACK FOOTER //
// ./src/components/pages/DataSafe.js